import { useTranslation } from "react-i18next"

import type { HostedAgent } from "@/api/admin-api"

import { Shield, ShieldCheck } from "@/components/icons"
import { Button } from "@/components/ui/button"

import { isPolicyConfigured } from "../utils/access-policy"

// Access tab of the hosted-agent edit wizard: a read-only summary of the
// current AccessPolicy plus the entry point to the shared
// AccessPolicyDialog. Access saves go straight to PUT .../access — they are
// intentionally NOT part of the revision-guarded form save.

export function HostedAgentAccessTab({
  agent,
  onEditPolicy,
}: {
  agent: HostedAgent
  onEditPolicy: () => void
}) {
  const { t } = useTranslation()
  const accessConfigured = isPolicyConfigured(agent.access)

  let summary: string
  if (agent.access.unrestricted === true) {
    summary = t("adminDashboard.hostedAgents.accessTab.unrestricted", {
      defaultValue: "Unrestricted — everyone in the tenant.",
    })
  } else if (accessConfigured) {
    summary = t("adminDashboard.hostedAgents.accessTab.restricted", {
      defaultValue: "Restricted: {{requires}} allowed, {{blocked}} blocked.",
      requires:
        (agent.access.requires?.oids?.length ?? 0) + (agent.access.requires?.groups?.length ?? 0),
      blocked: (agent.access.block?.oids?.length ?? 0) + (agent.access.block?.groups?.length ?? 0),
    })
  } else {
    summary = t("adminDashboard.hostedAgents.accessTab.notConfigured", {
      defaultValue: "Not configured — the agent is not visible to any user yet.",
    })
  }

  return (
    <div className="space-y-3">
      <p className="text-muted-foreground text-sm">
        {t("adminDashboard.hostedAgents.accessTab.help", {
          defaultValue:
            "Control which users and groups can see and use this agent. Access changes save immediately — they are not part of the form save.",
        })}
      </p>
      <div className="flex flex-wrap items-center gap-3 rounded-md border p-3">
        {accessConfigured ? (
          <ShieldCheck className="text-foreground size-5" />
        ) : (
          <Shield className="text-muted-foreground size-5" />
        )}
        <div className="min-w-0 flex-1 text-sm">{summary}</div>
        <Button type="button" variant="outline" onClick={onEditPolicy}>
          {t("adminDashboard.hostedAgents.accessTab.edit", {
            defaultValue: "Edit access policy…",
          })}
        </Button>
      </div>
    </div>
  )
}
